/**
 * src/pages/OriginAndBeyond/sections/InteractiveJourney.tsx
 * Interactive 4-stage inquiry journey:
 * - Glowing curved SVG path with 4 numbered nodes on desktop (horizontal arc)
 * - Vertical responsive stepper on mobile screens
 * - Full keyboard navigation (Left/Right/Up/Down arrows, Home/End, Space/Enter)
 * - Node highlight states with animated detail panel
 * - Three "what you'll explore" bullets per question
 * - "Enter question N" Link to target page
 * - Prev/Next navigation buttons
 */

import React, { useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Compass, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { JOURNEY } from "@/lib/originBeyond/journey";
import { JOURNEY_DETAILS, type JourneyDetail } from "../originAndBeyondData";
import "@/components/cosmic/cosmic.css";
export function InteractiveJourney() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const currentJourney = JOURNEY[selectedIndex];
  const currentDetail: JourneyDetail = JOURNEY_DETAILS[selectedIndex];
  const Icon = currentJourney.icon;
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % JOURNEY.length);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + JOURNEY.length) % JOURNEY.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setSelectedIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setSelectedIndex(JOURNEY.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSelectedIndex(index);
    }
  };
  const handlePrev = () => {
    setSelectedIndex(prev => prev > 0 ? prev - 1 : JOURNEY.length - 1);
  };
  const handleNext = () => {
    setSelectedIndex(prev => prev < JOURNEY.length - 1 ? prev + 1 : 0);
  };
  return <section id="journey-section" className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 scroll-mt-20" aria-labelledby="journey-heading">
      {/* Section Header */}
      <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-mono mb-3">
          <Sparkles className="size-3.5" aria-hidden="true" />
          <span>FOUR-STAGE EXPEDITION</span>
        </div>
        <h2 id="journey-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-3">
          The Four Foundational Questions
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
          Traverse four interconnected frontiers of human understanding. Choose an inquiry node
          to preview its scope or proceed directly into its investigation.
        </p>
      </div>

      {/* Desktop Curved Glowing Path (hidden on small screens, shown md+) */}
      <div className="hidden md:block relative w-full mb-10 px-6" aria-hidden="true">
        <svg viewBox="0 0 1000 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-24 overflow-visible">
          <defs>
            <linearGradient id="journeyGlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.8" />
              <stop offset="33%" stopColor="hsl(215 100% 60%)" stopOpacity="0.8" />
              <stop offset="66%" stopColor="hsl(270 70% 60%)" stopOpacity="0.8" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.8" />
            </linearGradient>
            <filter id="laserGlowFilter" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Background subtle guide path */}
          <path d="M 50 60 Q 275 10, 500 60 T 950 60" stroke="var(--border)" strokeWidth="2" strokeDasharray="4 4" fill="none" />

          {/* Active laser glow curve */}
          <path d="M 50 60 Q 275 10, 500 60 T 950 60" stroke="url(#journeyGlowGradient)" strokeWidth="3" filter="url(#laserGlowFilter)" fill="none" className="opacity-75" />
        </svg>

        {/* 4 Interactive Node Buttons along the curve */}
        <div role="tablist" aria-label="Expedition questions" className="absolute inset-0 flex items-center justify-between px-4">
          {JOURNEY.map((item, idx) => {
          const isSelected = idx === selectedIndex;
          const ItemIcon = item.icon;
          return <button key={item.id} type="button" role="tab" id={`journey-tab-${item.id}`} aria-selected={isSelected} aria-controls={`journey-panel-${item.id}`} tabIndex={isSelected ? 0 : -1} onClick={() => setSelectedIndex(idx)} onKeyDown={e => handleKeyDown(e, idx)} className={`relative flex flex-col items-center group cursor-pointer transition-transform duration-200 focus:outline-none ${isSelected ? "scale-110" : "hover:scale-105"}`}>
                <div className={`size-14 sm:size-16 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isSelected ? "border-primary bg-primary/20 shadow-[0_0_24px_var(--glow-primary)] text-primary" : "border-border bg-card/80 text-muted-foreground hover:border-primary/60 hover:text-foreground"}`}>
                  <ItemIcon className="size-6 sm:size-7" aria-hidden="true" />
                </div>

                <div className="mt-2 text-center min-w-0 max-w-[160px]">
                  <span className={`block font-mono text-[11px] uppercase tracking-wider font-bold transition-colors ${isSelected ? "text-primary" : "text-muted-foreground"}`}>
                    Stage 0{item.number}
                  </span>
                  <span className={`block font-serif text-xs font-semibold break-words leading-tight ${isSelected ? "text-foreground" : "text-muted-foreground/80"}`} title={item.short}>
                    {item.short}
                  </span>
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Mobile Vertical Stepper (shown below md) */}
      <div role="tablist" aria-label="Expedition questions" className="md:hidden flex flex-col gap-2 mb-6">
        {JOURNEY.map((item, idx) => {
        const isSelected = idx === selectedIndex;
        const ItemIcon = item.icon;
        return <button key={item.id} type="button" role="tab" id={`journey-tab-m-${item.id}`} aria-selected={isSelected} aria-controls={`journey-panel-${item.id}`} tabIndex={isSelected ? 0 : -1} onClick={() => setSelectedIndex(idx)} onKeyDown={e => handleKeyDown(e, idx)} className={`w-full flex items-center justify-between p-3.5 rounded-xl border text-left min-h-[52px] transition-all ${isSelected ? "border-primary/60 bg-primary/10 shadow-[0_0_16px_var(--glow-primary)]" : "border-border/50 bg-card/40 text-muted-foreground hover:border-border"}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`size-9 rounded-full flex items-center justify-center shrink-0 border ${isSelected ? "border-primary bg-primary/20 text-primary" : "border-border/60 bg-card text-muted-foreground"}`}>
                  <ItemIcon className="size-4" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-primary font-semibold">
                    Question {item.number}
                  </div>
                  <div className="font-serif text-sm font-bold text-foreground break-words leading-snug" title={item.title}>
                    {item.title}
                  </div>
                </div>
              </div>
              <span className={`font-mono text-xs px-2 py-0.5 rounded ${isSelected ? "text-primary" : "text-muted-foreground/60"}`}>
                0{item.number}
              </span>
            </button>;
      })}
      </div>

      {/* Selected Question Detail Panel */}
      <div key={currentJourney.id} id={`journey-panel-${currentJourney.id}`} role="tabpanel" aria-labelledby={`journey-tab-${currentJourney.id}`} className="ql-glass-strong p-6 sm:p-8 lg:p-10 rounded-2xl border border-primary/30 flex flex-col gap-6 shadow-[0_16px_40px_-10px_rgba(0,0,0,0.8)] animate-scroll-reveal">
        {/* Top telemetry and pagination arrows */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-border/40">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs font-bold uppercase tracking-widest text-primary px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
              Inquiry 0{currentJourney.number} of 04
            </span>
            <EvidenceBadge level={currentDetail.badgeLevel} />
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={handlePrev} className="size-9 rounded-full border border-border bg-card/60 flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors" aria-label="Previous question">
              <ArrowLeft className="size-4" />
            </button>
            <span className="font-mono text-xs text-muted-foreground px-2">
              {selectedIndex + 1} / {JOURNEY.length}
            </span>
            <button type="button" onClick={handleNext} className="size-9 rounded-full border border-border bg-card/60 flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors" aria-label="Next question">
              <ArrowRight className="size-4" />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="size-11 rounded-xl border border-primary/30 bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <Icon className="size-6" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold text-foreground tracking-tight">
                  {currentJourney.title}
                </h3>
                <p className="font-mono text-xs text-muted-foreground mt-0.5">
                  {currentDetail.subtitle}
                </p>
              </div>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">
              {currentDetail.teaser}
            </p>

            {/* Direct action link to question page */}
            <div className="pt-2">
              <Link to={currentJourney.href} className="ql-btn-neon inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[46px] font-serif text-xs sm:text-sm tracking-wider uppercase font-bold no-underline group">
                <span>Enter Question {currentJourney.number}</span>
                <ArrowUpRight className="size-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Three "What you'll explore" Bullets */}
          <div className="lg:col-span-5 p-5 sm:p-6 rounded-xl border border-border/60 bg-card/50 flex flex-col gap-3">
            <div className="flex items-center gap-2 mb-1">
              <Compass className="size-4 text-primary shrink-0" aria-hidden="true" />
              <h4 className="font-serif text-xs uppercase tracking-widest font-bold text-foreground">
                What You'll Explore
              </h4>
            </div>

            <ul className="space-y-3 p-0 m-0 list-none">
              {currentDetail.bullets.map((bullet, bIdx) => <li key={bIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  <span className="font-mono text-primary font-bold shrink-0 pt-0.5">
                    0{bIdx + 1}.
                  </span>
                  <span className="break-words font-sans">{bullet}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}